/**
 * 인식 언어(스캔이 대조할 카드 언어판) 상태.
 *
 * ⚠ UI 언어와 별개다. 한국어 앱을 쓰면서 일본판 카드를 모으는 사용자가 정상적으로 존재한다.
 *
 * 왜 고르게 하나 — 온디바이스 인덱스를 언어별로 나눠 배달하기 때문이다(ko 7.4MB vs 전량 30MB).
 * 대신 **언어가 어긋나면 인식이 조용히 틀린다**: 다른 언어판 카드를 스캔하면 실측 최대 37%가
 * 엉뚱한 카드로 확정된다(마진 게이트가 막아주지 않는다 — 후보가 줄어 오히려 마진이 넓어진다).
 * 그래서 전환 수단이 카메라 화면에 있어야 한다. monprice 도 같은 자리에 둔다.
 */
import { create } from "zustand";

export const SCAN_LANGS = ["ko", "ja", "en"] as const;
export type ScanLang = (typeof SCAN_LANGS)[number];

export const SCAN_LANG_STORAGE_KEY = "peeka.scanLang";

/** 칩·선택지에 그대로 쓰는 표기 — 각 언어를 그 언어로 적는다(번역 대상이 아니다). */
export const SCAN_LANG_LABEL: Record<ScanLang, string> = {
  ko: "한국어",
  ja: "日本語",
  en: "English",
};

/** 칩에 들어갈 짧은 표기. */
export const SCAN_LANG_SHORT: Record<ScanLang, string> = {
  ko: "KO",
  ja: "JA",
  en: "EN",
};

function isScanLang(value: unknown): value is ScanLang {
  return typeof value === "string" && (SCAN_LANGS as readonly string[]).includes(value);
}

/** 언어팩이 없는 경우의 귀착점 — 카드 원산 표기가 영어권과 가장 넓게 겹친다. */
export const SCAN_LANG_FALLBACK: ScanLang = "en";

/** 기기 로케일 → 인식 언어. 우리가 내는 셋 중 어디에도 안 걸리면 영어로 둔다. */
function fromLocale(): ScanLang {
  const tags = typeof navigator === "undefined" ? [] : (navigator.languages ?? [navigator.language]);
  for (const tag of tags) {
    const base = String(tag).toLowerCase().split("-")[0];
    if (isScanLang(base)) return base;
  }
  return SCAN_LANG_FALLBACK;
}

function readStored(): ScanLang | null {
  try {
    const raw = localStorage.getItem(SCAN_LANG_STORAGE_KEY);
    return isScanLang(raw) ? raw : null;
  } catch {
    return null;
  }
}

interface ScanLangState {
  lang: ScanLang;
  /** 사용자가 직접 고른 적이 있는가 — 로케일 추정과 명시 선택을 구분한다. */
  chosen: boolean;
  setLang: (lang: ScanLang) => void;
}

export const useScanLangStore = create<ScanLangState>((set) => {
  const stored = readStored();
  return {
    lang: stored ?? fromLocale(),
    chosen: stored !== null,
    setLang: (lang) => {
      try {
        localStorage.setItem(SCAN_LANG_STORAGE_KEY, lang);
      } catch {
        /* private mode 등 — 저장 실패는 무시 */
      }
      set({ lang, chosen: true });
    },
  };
});
